import type { OnInit, Type } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AmlAdminErrorHandlerService } from '@core/services/aml-admin-error-handler.service';
import type { TabComponent } from '@core/types/tab-component.types';
import { environment } from '@env';
import { TrainingListComponent } from '@features/academy/views/training-list/training-list.component';
import { TrainingUserListComponent } from '@features/academy/views/training-user-list/training-user-list.component';
import { AuthenticationFacade } from '@features/authentication/facades/authentication.facade';
import { E2EAuthenticationService } from '@features/authentication/services/e2e-authentication.service';
import { ContractsDetailComponent } from '@features/organization/components/contracts-detail/contracts-detail.component';
import { DeliveryTrackerDetailComponent } from '@features/organization/components/delivery-tracker-detail/delivery-tracker-detail.component';
import { OrganizationDetailTabEmailList } from '@features/organization/components/detail-tabs/organization-detail-tab-email-list/organization-detail-tab-email-list';
import { ErrorSeverityMap } from '@kerberos-compliance/compliance-shared-ui';

@Component({
  selector: 'aad-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
  imports: [RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  /**
   * An array of Components that holds all Components that are used as tabs.
   * It's necessary to add them here that they are loaded.
   */
  protected readonly tabComponents: Type<TabComponent>[] = [
    TrainingListComponent,
    TrainingUserListComponent,
    DeliveryTrackerDetailComponent,
    ContractsDetailComponent,
    OrganizationDetailTabEmailList,
  ];

  private readonly InitializationErrorMetadata = {
    severity: ErrorSeverityMap.High,
    component: 'AppComponent',
    action: 'Error initializing application.',
  } as const;

  private readonly authenticationFacade = inject(AuthenticationFacade);

  private readonly e2eAuthenticationService = inject(E2EAuthenticationService);

  private readonly errorHandlerService = inject(AmlAdminErrorHandlerService);

  public ngOnInit(): void {
    void this.initializeApplicationWithErrorHandling();
  }

  /**
   * Initializes the application and incorporates error handling.
   *
   * @returns {Promise<void>} A promise that resolves once initialization has completed, whether it
   * succeeded or its error was reported.
   */
  private async initializeApplicationWithErrorHandling(): Promise<void> {
    try {
      await this.initializeApplication();
    } catch (error) {
      this.errorHandlerService.handleError({
        error,
        metadata: this.InitializationErrorMetadata,
      });
    }
  }

  /**
   * Initializes the application by setting up Azure login (MSAL) or E2E authentication.
   *
   * @returns {Promise<void>} A promise that resolves when application initialization is complete.
   */
  private async initializeApplication(): Promise<void> {
    await (environment.enableMsal
      ? this.authenticationFacade.initializeMsal()
      : this.e2eAuthenticationService.handleE2EAuthentication());
  }
}
